import PromiseQueue from 'promise-queue';
import { fetchAccessTokenIfNeeded } from '~/AccessToken';
import { retryFetchAfterUserProductGroupLock } from '~/Api/User/ProductGroup';
import {
	CaLabelingResult,
	ErrorResponse,
	findCaLabeling,
	ProductUpdate,
	updateCaLabeling as apiUpdateCaLabeling,
} from '~/Api/User/ProductGroup/CaLabeling';
import { invokeApi, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { fetchGroup } from '~/ProductGroup';
import { ThunkAction } from '~/Store';

import { Action, ActionTypes } from './Action';

const updateQueue = new PromiseQueue(1, Infinity);

export const fetchCaLabeling =
	(groupId: string): ThunkAction<Promise<CaLabelingResult>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = dispatch(invokeApi(findCaLabeling, userId, groupId));
		dispatch({ type: ActionTypes.request, groupId, request });

		return resolve(request, (response) =>
			dispatch({
				groupId,
				caLabeling: response?.caLabeling ?? null,
				request,
				type: ActionTypes.receive,
			})
		);
	};

export const updateCaLabeling =
	(groupId: string, basketVersion: string, products: ProductUpdate[]): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const request = updateQueue.add(() => dispatch(immediatelyUpdateCaLabeling(groupId, basketVersion, products)));
		dispatch({ type: ActionTypes.requestUpdate, groupId, request });
		return request;
	};

export const immediatelyUpdateCaLabeling =
	(groupId: string, basketVersion: string, products: ProductUpdate[]): ThunkAction<Promise<void>, Action> =>
	async (dispatch) => {
		const userId = (await dispatch(fetchAccessTokenIfNeeded())).accessToken.user.id;
		const request = retryFetchAfterUserProductGroupLock(() =>
			dispatch(invokeApi(apiUpdateCaLabeling, userId, groupId, basketVersion, products))
		);
		dispatch({ type: ActionTypes.requestUpdate, groupId, request });

		await resolve(request, (response) =>
			dispatch({
				groupId,
				caLabeling: response?.caLabeling ?? null,
				request,
				type: ActionTypes.receiveUpdate,
			})
		);
	};

export const handleCaLabelingUpdateResponse =
	<Response>(response: Response, groupId: string): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(response instanceof ErrorResponse)) {
			dispatch(handleError(response));
			return;
		}
		if (response.handleProductGroupNotFound()) {
			dispatch(fetchGroup(groupId));
		}
		if (response.handleCannotChangeSettingsForNonMemberProducts()) {
			dispatch(fetchCaLabeling(groupId)).catch(handleError);
		}
		dispatch(handleError(response));
	};

export const pruneCaLabeling = (groupId: string): Action => ({
	type: ActionTypes.prune,
	groupId,
});

export const scheduleCaLabelingUpdate =
	(groupId: string, basketVersion: string, products: ProductUpdate[]): ThunkAction<Promise<void>, Action> =>
	async (dispatch) =>
		new Promise((resolve) => {
			const timeoutId = setTimeout(() => {
				resolve(dispatch(updateCaLabeling(groupId, basketVersion, products)));
			}, 2000);

			dispatch({
				basketVersion,
				groupId,
				products,
				timeoutId,
				type: ActionTypes.scheduleUpdate,
			});
		});

export const commitCaLabelingUpdate =
	(groupId: string): ThunkAction<Promise<void>, Action> =>
	async (dispatch, getState) => {
		const { update } = getState().caLabeling;
		if (update?.groupId !== groupId) {
			return;
		}
		if (update.state.name === 'fetching') {
			await update.state.request;
		}
		if (update.state.name === 'scheduled') {
			const { timeoutId, basketVersion, products } = update.state;
			clearTimeout(timeoutId);
			await dispatch(updateCaLabeling(groupId, basketVersion, products));
		}
	};

export const handleCommitError =
	<Error>(error: Error): ThunkAction<void, Action> =>
	(dispatch) => {
		if (!(error instanceof ErrorResponse)) {
			dispatch(handleError(error));
			return;
		}

		error.handleProductGroupNotFound();
		error.handleCannotChangeSettingsForNonMemberProducts();
		dispatch(handleError(error));
	};
